import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { BookOpen, CircleCheck, CircleDashed } from 'lucide-react';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DetailTableLayout } from '@/components/common/DetailTableLayout';
import { LabeledPillList, type PillField } from '@/components/common/LabeledPill';
import { useRememberViewedRow } from '@/hooks/useLastViewedRow';
import { QUESTION_PERMISSIONS } from '@/constants/permissions';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { instituteSubjectsQueryOptions, questionQueryKeys, resetPapers } from '../api/question.api';
import { getMonitorSubjectColumns } from '../components/QuestionColumns';
import { MONITOR_SCREENS } from '../constants/question.constants';
import { useInstituteHolder } from '../hooks/useInstituteHolder';
import type { InstituteSubject, MonitorSubjectsSearch } from '../types/question.types';
import { downloadPaperWithFeedback } from '../utils/downloadPaperWithFeedback';
import { getHolderPills } from '../utils/holderPills';
import {
  getMonitorScreenByPath,
  getSubjectListTitle,
  toCountPillField,
} from '../utils/monitorLevels';

function getSubjectCountPills(
  subjects: InstituteSubject[] | undefined,
  isLoading: boolean,
): PillField[] {
  const collectedCount = subjects?.filter((subject) => subject.isCollected).length;
  const notCollectedCount =
    subjects === undefined || collectedCount === undefined
      ? undefined
      : subjects.length - collectedCount;

  return [
    toCountPillField({
      key: 'subjects',
      label: 'Subjects',
      count: subjects?.length,
      isLoading,
      icon: BookOpen,
    }),
    toCountPillField({
      key: 'collected',
      label: 'Collected',
      count: collectedCount,
      isLoading,
      icon: CircleCheck,
    }),
    toCountPillField({
      key: 'notCollected',
      label: 'Not Collected',
      count: notCollectedCount,
      isLoading,
      icon: CircleDashed,
    }),
  ];
}

interface MonitorInstituteSubjectsPageProps {
  projectInstituteId: number;
  search: MonitorSubjectsSearch;
}

export function MonitorInstituteSubjectsPage({
  projectInstituteId,
  search,
}: MonitorInstituteSubjectsPageProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [resettingSubject, setResettingSubject] = useState<InstituteSubject | null>(null);

  useRememberViewedRow('questionMonitorInstitutes', projectInstituteId);

  const { showDownloadCount } = MONITOR_SCREENS[getMonitorScreenByPath(search.from)];
  const { can } = usePermissions();
  const subjectsQuery = useQuery(
    instituteSubjectsQueryOptions(projectInstituteId, {
      subjectType: search.subjectType,
      ...(search.isNew === undefined ? {} : { isNew: search.isNew }),
    }),
  );
  const { holder, isLoading: isHolderLoading } = useInstituteHolder(projectInstituteId, search);

  const columns = useMemo(
    () =>
      getMonitorSubjectColumns(
        {
          onDownload: (subject) => {
            void downloadPaperWithFeedback(subject.uploadId, subject.fileName);
          },
          onReset: setResettingSubject,
          canDownload: can(QUESTION_PERMISSIONS.UPLOAD_DOWNLOAD),
          canReset: can(QUESTION_PERMISSIONS.UPLOAD_RESET),
        },
        showDownloadCount,
      ),
    [can, showDownloadCount],
  );

  function handleResetConfirm() {
    if (resettingSubject === null) return;
    void resetPapers(resettingSubject.instituteSubjectId).then(async () => {
      setResettingSubject(null);
      await queryClient.invalidateQueries({ queryKey: questionQueryKeys.all() });
    });
  }

  return (
    <>
      <DetailTableLayout
        title={getSubjectListTitle(search.subjectType, search.isNew)}
        onNoHistory={() =>
          void navigate({ to: search.from, search: { projectId: search.projectId } })
        }
        summary={
          <LabeledPillList
            fields={[
              ...getHolderPills(holder, isHolderLoading),
              ...getSubjectCountPills(subjectsQuery.data, subjectsQuery.isLoading),
            ]}
          />
        }
        table={{
          rows: subjectsQuery.data ?? [],
          columns,
          isLoading: subjectsQuery.isLoading,
          error: subjectsQuery.error,
          errorTitle: 'Could not load the subjects',
          onRetry: () => void subjectsQuery.refetch(),
          stickyColumnId: 'subjectName',
          emptyMessage: 'No subject sits under this figure',
        }}
      />

      <ConfirmDialog
        open={resettingSubject !== null}
        title="Reset this attachment?"
        description={`Every paper collected for ${
          resettingSubject?.subjectName ?? 'this subject'
        } is dropped, along with who has seen it, and the subject reads as never collected. This cannot be undone.`}
        confirmLabel="Reset"
        confirmVariant="destructive"
        onConfirm={handleResetConfirm}
        onOpenChange={(open) => {
          if (!open) setResettingSubject(null);
        }}
      />
    </>
  );
}
